import type { ActivityEvent, Execution, WorkItemDetail, WorkItemInTraining, WorkItemPlan } from '@deskworks/shared-types';
import { TERMINAL_WORK_ITEM_STATUSES } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlarmClock,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Circle,
  CircleDot,
  Clock,
  Crown,
  GitBranch,
  GraduationCap,
  ListChecks,
  Loader2,
  MessageCircleQuestion,
  Pause,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { formatAbsolute, formatRelative, formatSince } from '@/lib/format';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { isAgentRunning } from './chat';
import { ownLeadRuns, scrollToRun } from './work-log';

/** A person took the item into the desk's training: it rests here until the Lead closes it there (docs/features/desk-training.md). */
export function inTraining(item: WorkItemDetail): WorkItemInTraining | null {
  const training = item.resultMetadata?.training as WorkItemInTraining | undefined;
  return training?.conversationId && !TERMINAL_WORK_ITEM_STATUSES.includes(item.status) ? training : null;
}

type Tone = 'indigo' | 'amber' | 'red' | 'emerald' | 'sky' | 'neutral';

const TONE: Record<Tone, { card: string; badge: string }> = {
  indigo: { card: 'border-indigo-200 dark:border-indigo-900', badge: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
  amber: { card: 'border-amber-300 dark:border-amber-800', badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  red: { card: 'border-red-300 dark:border-red-900', badge: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  emerald: { card: 'border-emerald-200 dark:border-emerald-900', badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  sky: { card: 'border-sky-200 dark:border-sky-900', badge: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  neutral: { card: '', badge: 'bg-muted text-muted-foreground' },
};

interface Headline {
  tone: Tone;
  icon: ReactNode;
  title: string;
  /** What it means / what happens next. */
  detail?: ReactNode;
}

/** The one sentence that says where the work stands — who has it and what they're waiting for. */
function headline(item: WorkItemDetail, latestStep: string | null): Headline {
  const agent = item.currentExecution?.agentName ?? item.assignedAgentName ?? t('The agent');
  const lead = item.lead?.name ?? t('the Lead');
  const running = isAgentRunning(item);
  const training = inTraining(item);

  if (training) {
    return {
      tone: 'sky',
      icon: <GraduationCap />,
      title: t('In training with {lead}', { lead }),
      detail: (
        <>
          {t('It rests here until the Lead closes it in the training.')}{' '}
          <Link
            to="/w/$workspaceId/desks/$deskId"
            params={{ workspaceId: item.workspaceId, deskId: item.deskId }}
            search={{ tab: 'training' }}
            className="font-medium underline underline-offset-2"
          >
            {t('Open training')}
          </Link>
        </>
      ),
    };
  }
  if (item.status === 'complete') {
    return {
      tone: 'emerald',
      icon: <CheckCircle2 />,
      title: t('Done'),
      detail: item.completedAt ? <span title={formatAbsolute(item.completedAt)}>{t('Finished {time}.', { time: formatRelative(item.completedAt) })}</span> : undefined,
    };
  }
  if (item.status === 'cancelled') return { tone: 'neutral', icon: <XCircle />, title: t('Cancelled'), detail: t('Nobody works on it any more.') };
  if (item.status === 'failed') {
    const err = item.currentExecution?.error;
    return {
      tone: 'red',
      icon: <AlertTriangle />,
      title: t('That didn’t work'),
      detail: (
        <>
          {err ? <span className="font-mono text-xs break-words">{err}</span> : t('The work failed.')}{' '}
          {t('You can start it again with “Try again”. You’ll find details under “Technical details”.')}
        </>
      ),
    };
  }
  if (item.status === 'blocked') {
    return {
      tone: 'red',
      icon: <AlertTriangle />,
      title: item.waitingReason ? t('Blocked: {reason}', { reason: item.waitingReason }) : t('Blocked'),
      detail: item.waitingReason
        ? t('The agent needs something that is missing. Fix it (e.g. add the connection to the Desk), then click “Continue”.')
        : t('This Work Item can’t continue right now — often because of a dependency. Check “Dependencies” on the right.'),
    };
  }
  if (item.paused && item.program?.stoppedReason) {
    return {
      tone: 'amber',
      icon: <Pause />,
      title: item.program.stoppedReason === 'budget_day' ? t('Paused for today: budget reached') : t('Program stopped'),
      detail:
        item.program.stoppedReason === 'budget_day'
          ? t('It goes on tomorrow, or now when you raise the budget in the program’s settings. Work handed out goes on.')
          : item.program.stoppedReason === 'budget_total'
            ? t('Its budget is used up. Raise it in the program’s settings to go on. Work handed out goes on.')
            : t('Its end has come. Set a later end in the program’s settings to go on.'),
    };
  }
  if (item.paused) {
    return {
      tone: 'amber',
      icon: <Pause />,
      title: t('Paused'),
      detail: t('The agent was stopped; everything so far is kept. Click “Continue” and a fresh worker picks up from the saved state — it also reads any messages you write in the meantime.'),
    };
  }
  if (item.status === 'needs_approval') {
    const pending = item.approvals.find((a) => a.status === 'pending');
    return {
      tone: 'amber',
      icon: <ShieldCheck />,
      title: t('Waiting for your approval'),
      detail: pending?.type === 'result' ? t('The result waits for you: decide in the card at the end of the work history.') : t('Nothing goes on until someone decides — the card is at the end of the work history.'),
    };
  }
  if (running && item.currentExecution?.kind === 'follow_up') {
    return { tone: 'indigo', icon: <MessageCircleQuestion />, title: t('{agent} is answering in the chat', { agent }) };
  }
  if (running) {
    const since = formatSince(item.currentExecution?.startedAt ?? item.currentExecution?.createdAt);
    return {
      tone: 'indigo',
      icon: <Loader2 className="animate-spin" />,
      title: since ? t('{agent} is working on it — {since}', { agent, since }) : t('{agent} is working on it', { agent }),
      detail: latestStep ?? undefined,
    };
  }
  if (item.status === 'waiting') {
    if (item.waitingReason === 'wake' && item.wakeAt) {
      return { tone: 'sky', icon: <AlarmClock />, title: t('Looks again {time}', { time: formatAbsolute(item.wakeAt) }), detail: item.wakeReason ?? undefined };
    }
    if (item.waitingReason === 'children') {
      const { done, total } = childCounts(item);
      return {
        tone: 'neutral',
        icon: <GitBranch />,
        title: t('Waiting for sub-items'),
        detail: total
          ? item.program
            ? t('{done} of {total} done. VICKI goes on with each result.', { done, total })
            : t('{done} of {total} done. {lead} continues when all of them are done.', { done, total, lead })
          : t('The Lead handed parts of it on and continues when all of them are done.'),
      };
    }
    if (item.withLead && !item.questionOpen) {
      return {
        tone: 'neutral',
        icon: <Crown />,
        title: t('{lead} decides the next step', { lead }),
        detail: item.waitingReason || t('Every run reports back to the desk’s Lead, which checks the result and starts the next run or closes it. You can still write in the thread.'),
      };
    }
    return {
      tone: 'amber',
      icon: <Clock />,
      title: item.questionOpen ? t('Waiting for your answer') : t('The agent is waiting for you'),
      detail: item.waitingReason || t('Answer at the end of the work history — the work goes on once it is answered.'),
    };
  }
  if (item.status === 'ready' || item.status === 'working') {
    return { tone: 'indigo', icon: <Clock />, title: t('{agent} takes it up shortly', { agent }), detail: t('A worker is being started.') };
  }
  if (!item.assignedAgentId) {
    return {
      tone: 'neutral',
      icon: <Clock />,
      title: t('Not started yet'),
      detail: t('The Desk’s Lead takes it in, plans it and starts the first run. You can also assign an agent via “Edit”, then click “Start”.'),
    };
  }
  return { tone: 'neutral', icon: <Clock />, title: t('Not started yet'), detail: t('Click “Start” and {agent} begins.', { agent }) };
}

function childCounts(item: WorkItemDetail) {
  const tasks = item.children.filter((c) => c.kind !== 'review');
  return { done: tasks.filter((c) => TERMINAL_WORK_ITEM_STATUSES.includes(c.status)).length, total: tasks.length };
}

type StepState = 'done' | 'now' | 'open';
interface PlanStep {
  text: string;
  state: StepState;
}

const DONE_MARK = /^(✅|✔️|✔|☑️|☑|\[x\])\s*/i;
const NOW_MARK = /^(🚀|🔄|▶️|▶|⏩|🏃|⚙️|🛠️|🔧|👉|\[~\]|\[-\])\s*/;
const OPEN_MARK = /^(⏳|⬜|🔲|☐|◻️|\[ \])\s*/;

/**
 * The steps of a plan that marks them (✅ done, 🚀 in progress, ⏳ open, or Markdown checkboxes). A plan without marks
 * says nothing about how far it is — then null.
 */
export function planSteps(plan: string | null | undefined): PlanStep[] | null {
  if (!plan?.trim()) return null;
  const steps: PlanStep[] = [];
  let marked = 0;
  for (const line of plan.split('\n')) {
    const m = /^\s*(?:\d+[.)]|[-*+])\s+(.*)$/.exec(line);
    if (!m) continue;
    let text = m[1].trim();
    let state: StepState = 'open';
    for (const [re, s] of [
      [DONE_MARK, 'done'],
      [NOW_MARK, 'now'],
      [OPEN_MARK, 'open'],
    ] as const) {
      if (re.test(text)) {
        text = text.replace(re, '');
        state = s;
        marked++;
        break;
      }
    }
    steps.push({ text: text.replace(/\*\*|__|`/g, ''), state });
  }
  return marked >= 2 ? steps : null;
}

/**
 * Where the work stands, at a glance (docs/features/work-items.md#current-state): who has it now and what for, how
 * far the plan, the done criteria and the sub-items are, and what was done last.
 */
export function CurrentState({ item, activity }: { item: WorkItemDetail; activity: ActivityEvent[] | undefined }) {
  const running = isAgentRunning(item);
  // What the agent at work said last about what it does.
  const step = running ? [...(activity ?? [])].reverse().find((e) => e.type === 'agent.step' && e.executionId === item.currentExecution?.id) : undefined;
  const head = headline(item, step?.message ?? null);
  const tone = TONE[head.tone];
  const steps = planSteps(item.plan);
  const now = steps?.find((s) => s.state === 'now');
  const next = steps?.find((s) => s.state === 'open');
  const criteria = item.doneCriteria ?? [];
  const children = childCounts(item);
  const lastEvent = activity?.at(-1);

  return (
    <section className={cn('rounded-xl border bg-card p-4 shadow-xs sm:p-5', tone.card)} aria-label={t('Current state')}>
      <div className="mb-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="font-medium tracking-wide uppercase">{t('Current state')}</span>
        {lastEvent && (
          <span title={formatAbsolute(lastEvent.createdAt, true)}>
            {t('Last activity')} <RelativeTime value={lastEvent.createdAt} />
          </span>
        )}
      </div>
      <div className="flex items-start gap-3">
        <div className={cn('flex size-9 shrink-0 items-center justify-center rounded-full [&_svg]:size-4.5', tone.badge)}>{head.icon}</div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base leading-snug font-semibold">{head.title}</h2>
          {head.detail && <div className="mt-0.5 line-clamp-4 text-sm break-words text-muted-foreground">{head.detail}</div>}
        </div>
      </div>

      {(steps || criteria.length > 0 || children.total > 0) && (
        <div className="mt-4 grid gap-3">
          {steps && <Meter label={t('Plan')} done={steps.filter((s) => s.state === 'done').length} total={steps.length} unit={t('steps')} />}
          {criteria.length > 0 && <Meter label={t('Done criteria')} done={criteria.filter((c) => c.done).length} total={criteria.length} unit={t('met')} />}
          {children.total > 0 && <Meter label={t('Sub-items')} done={children.done} total={children.total} unit={t('finished')} />}
        </div>
      )}

      {(now || next) && (
        <ul className="mt-4 flex flex-col gap-1.5 text-sm">
          {now && (
            <li className="flex items-start gap-2">
              <CircleDot className="mt-0.5 size-4 shrink-0 text-indigo-500" />
              <span className="min-w-0">
                <span className="font-medium">{t('Now:')}</span> <span className="break-words">{now.text}</span>
              </span>
            </li>
          )}
          {next && (
            <li className="flex items-start gap-2 text-muted-foreground">
              <Circle className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0">
                <span className="font-medium">{t('Next:')}</span> <span className="break-words">{next.text}</span>
              </span>
            </li>
          )}
        </ul>
      )}

      {item.wakeAt && <WakeLine item={item} />}

    </section>
  );
}

/** When the item looks at itself again (docs/features/work-items.md#waking-again), with a way to stop it. */
function WakeLine({ item }: { item: WorkItemDetail }) {
  const stop = useApiMutation(() => api.workItems.stopWake(item.id), { invalidate: [qk.workItem(item.id)], success: t('It looks at nothing again') });
  const every = item.wakeEveryMinutes
    ? item.wakeUntil
      ? t('then every {n} min until {until}', { n: item.wakeEveryMinutes, until: formatAbsolute(item.wakeUntil) })
      : t('then every {n} min', { n: item.wakeEveryMinutes })
    : null;
  return (
    <div className="mt-4 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2 text-[13px] dark:border-sky-900 dark:bg-sky-950/30">
      <AlarmClock className="mt-0.5 size-4 shrink-0 text-sky-600 dark:text-sky-300" />
      <div className="min-w-0 flex-1">
        <span className="font-medium">{t('Looks again {time}', { time: formatAbsolute(item.wakeAt) })}</span>
        {every && <span className="text-muted-foreground"> · {every}</span>}
        {item.wakeReason && <div className="break-words text-muted-foreground">{item.wakeReason}</div>}
      </div>
      <Button size="xs" variant="ghost" onClick={() => stop.mutate()} loading={stop.isPending} title={t('Stop looking again')}>
        {t('Stop')}
      </Button>
    </div>
  );
}

function Meter({ label, done, total, unit }: { label: string; done: number; total: number; unit: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">
          {t('{done} of {total}', { done, total })} <span className="font-normal text-muted-foreground">{unit}</span>
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} aria-label={label}>
        <div className={cn('h-full rounded-full transition-all', done === total ? 'bg-emerald-500' : 'bg-indigo-500')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * The plan beside the thread. A plan that marks its steps (✅ 🚀 ⏳) shows them as a checklist with where the work is;
 * any other plan as written.
 */
export function PlanPanel({ item }: { item: WorkItemDetail }) {
  // A Lead's plan with steps and versions (docs/features/work-items.md#the-plan); older items have the plan as text.
  const plans = useQuery({ queryKey: qk.workItemPlans(item.id), queryFn: () => api.workItems.plans(item.id), enabled: item.kind === 'task' });
  const runs = useQuery({ queryKey: qk.workItemExecutions(item.id), queryFn: () => api.workItems.executions(item.id) });
  if (plans.data?.length) return <StepsPlan plans={plans.data} runs={runs.data ?? []} />;
  return <TextPlan item={item} runs={runs.data ?? []} />;
}

/** The plan with its steps: who does what, the result expected, the data it must carry, how far it is, its runs. */
function StepsPlan({ plans, runs }: { plans: WorkItemPlan[]; runs: Execution[] }) {
  const [plan, ...older] = plans;
  const done = plan.steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  return (
    <Section
      icon={<ListChecks />}
      title={t('Plan')}
      description={t('Version {version} · {done} of {total} steps done', { version: plan.version, done, total: plan.steps.length })}
      actions={plan.approvalStatus && <PlanApproval status={plan.approvalStatus} />}
    >
      {plan.intent && <p className="mb-2 text-[13px] font-medium">{plan.intent}</p>}
      {plan.changeNote && (
        <p className="mb-3 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground">
          {t('Changed in version {version}:', { version: plan.version })} <span className="text-foreground/85">{plan.changeNote}</span>
        </p>
      )}
      <ol className="flex flex-col gap-3">
        {plan.steps.map((step, i) => {
          const own = runs.filter((r) => r.planStep === step.id && !r.sideChat).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          return (
            <li key={step.id} className={cn('flex items-start gap-2 text-[13px]', (step.status === 'done' || step.status === 'skipped') && 'text-muted-foreground')}>
              {step.status === 'done' ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              ) : step.status === 'running' ? (
                <CircleDot className="mt-0.5 size-4 shrink-0 text-indigo-500" />
              ) : step.status === 'skipped' ? (
                <XCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground/60" />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground/60" />
              )}
              <div className="min-w-0 flex-1">
                <p className={cn('break-words', step.status === 'running' && 'font-medium text-foreground')}>
                  <span className="text-muted-foreground tabular-nums">{i + 1}.</span> {step.title}
                  {step.status === 'skipped' && <span className="ml-1 text-xs">({t('skipped')})</span>}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/75">{step.agentName ?? t('Lead')}</span>
                  {step.expectedOutput && <span className="break-words">→ {step.expectedOutput}</span>}
                </p>
                {step.requiredFields.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-1" title={t('The data its result must carry')}>
                    {step.requiredFields.map((f) => (
                      <span key={f} className="rounded bg-muted px-1.5 py-px font-mono text-[11px] text-muted-foreground">
                        {f}
                      </span>
                    ))}
                  </p>
                )}
                {own.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-x-2 text-xs">
                    {own.map((r, n) => (
                      <button key={r.id} type="button" onClick={() => scrollToRun(r.id)} className="cursor-pointer text-primary hover:underline" title={t('Show this run in the work history')}>
                        {own.length > 1 ? t('run {n}', { n: n + 1 }) : t('its run')}
                        {r.report?.headline ? `: ${r.report.headline.slice(0, 60)}${r.report.headline.length > 60 ? '…' : ''}` : ''}
                      </button>
                    ))}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {older.length > 0 && <OlderPlans plans={older} />}
    </Section>
  );
}

function PlanApproval({ status }: { status: NonNullable<WorkItemPlan['approvalStatus']> }) {
  const [label, tone] =
    status === 'approved'
      ? [t('Plan approved'), 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300']
      : status === 'rejected'
        ? [t('Plan rejected'), 'bg-red-500/15 text-red-700 dark:text-red-300']
        : [t('Plan waits for approval'), 'bg-amber-500/15 text-amber-800 dark:text-amber-300'];
  return <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', tone)}>{label}</span>;
}

/** Earlier versions of the plan: when, why it changed then, its steps. */
function OlderPlans({ plans }: { plans: WorkItemPlan[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t pt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {t('Earlier versions ({n})', { n: plans.length })}
      </button>
      {open && (
        <ol className="mt-2 flex flex-col gap-3">
          {plans.map((p) => (
            <li key={p.id} className="text-xs">
              <p className="font-medium">
                {t('Version {version}', { version: p.version })} · <RelativeTime value={p.createdAt} />
              </p>
              {p.changeNote && <p className="text-muted-foreground">{p.changeNote}</p>}
              <ol className="mt-1 list-decimal pl-5 text-muted-foreground">
                {p.steps.map((s) => (
                  <li key={s.id}>
                    {s.title} · {s.agentName ?? t('Lead')}
                  </li>
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A plan written as text: a plan that marks its steps (✅ 🚀 ⏳) as a checklist, any other as written. */
function TextPlan({ item, runs: allRuns }: { item: WorkItemDetail; runs: Execution[] }) {
  const steps = planSteps(item.plan);
  const runs = { data: allRuns };
  const stepRuns = steps ? runsOfSteps(item, steps, runs.data ?? []) : [];
  // The plan's own heading ("🗺️ Feature X, only to dev"), above the steps.
  const intro = steps ? item.plan!.split('\n').find((l) => l.trim() && !/^\s*(?:\d+[.)]|[-*+])\s+/.test(l))?.replace(/^#+\s*/, '').trim() : null;
  return (
    <Section icon={<ListChecks />} title={t('Plan')} description={t('How the Lead and its agents go about it. It is updated as the work goes on.')}>
      {!item.plan?.trim() ? (
        <p className="text-sm text-muted-foreground">{t('No plan yet — the Lead writes one when it takes the work in.')}</p>
      ) : steps ? (
        <>
          {intro && <p className="mb-3 text-[13px] font-medium">{intro.replace(/\*\*|__|`/g, '')}</p>}
          <ol className="flex flex-col gap-2">
            {steps.map((step, i) => (
              <li key={i} className={cn('flex items-start gap-2 text-[13px]', step.state === 'done' && 'text-muted-foreground')}>
                {step.state === 'done' ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                ) : step.state === 'now' ? (
                  <CircleDot className="mt-0.5 size-4 shrink-0 text-indigo-500" />
                ) : (
                  <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground/60" />
                )}
                <span className={cn('min-w-0 break-words', step.state === 'now' && 'font-medium')}>
                  {step.text}
                  {stepRuns[i] && (
                    <button
                      type="button"
                      onClick={() => scrollToRun(stepRuns[i]!)}
                      className="ml-1.5 cursor-pointer text-xs font-normal whitespace-nowrap text-primary hover:underline"
                      title={t('Show this step’s run in the thread')}
                    >
                      {t('its run')}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <Markdown text={item.plan} />
      )}
    </Section>
  );
}

/**
 * The run of each plan step that is done or in progress: a step names its owner ("Diagnosis (**Max**)", "(Lead)"), and
 * the k-th step of an agent is its k-th run — for the Lead, its k-th run of its own (not one taking a result in).
 */
function runsOfSteps(item: WorkItemDetail, steps: PlanStep[], runs: Execution[]): (string | null)[] {
  const queues = new Map<string, string[]>();
  for (const r of [...runs].filter((r) => r.kind === 'work_item' && !r.sideChat && r.agentName).sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    queues.set(r.agentName!, [...(queues.get(r.agentName!) ?? []), r.id]);
  }
  // Longest first, so "Case-Analyst" is not taken for "Analyst".
  const names = [...queues.keys()].sort((a, b) => b.length - a.length);
  const leadRuns = ownLeadRuns(item, runs);
  const lead = item.lead?.name;
  return steps.map((step) => {
    if (step.state === 'open') return null;
    const name = names.find((n) => step.text.includes(n));
    if (name) return queues.get(name)!.shift() ?? null;
    if ((lead && step.text.includes(lead)) || /\bLead\b/.test(step.text)) return leadRuns.shift() ?? null;
    return null;
  });
}
